/**
 * EWSCustomersTable - Paginated customer table
 *
 * Displays customer records with consent ID, balance, risk metrics,
 * EMI count, and SIPs. Supports pagination, sorting, and row click for drawer opening.
 * Meta columns from csv_meta are added as hideable columns — users toggle them
 * via the built-in "Manage columns" drawer (same UX as ConsentTable).
 * Column visibility is persisted per batch in localStorage.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePortalPopover } from '../../../hooks/usePortalPopover';
import { Table, type TableColumn, type TableSortState, Tag, Loader } from 'equal-ds-ui';
import { PAGE_SIZE_OPTIONS } from '../../../constants/pagination.constants';
import {
  EWSCustomersTableProps,
  EWSCustomer,
  formatINR,
  formatIndianNumber,
  RiskLevel,
  getRiskLevelColor,
  CustomerSortColumn,
} from '../../../types/ews.types';

const VISIBILITY_KEY = 'ews_col_visibility';
const ORDER_KEY = 'ews_col_order';

// Needs to exceed the table's sticky header z-index to render above it.
const COLUMN_HEADER_DROPDOWN_Z = 99999;

/**
 * Load global column visibility prefs from localStorage.
 * Only keys present in the current meta columns are returned,
 * so stale keys from removed columns are ignored.
 */
function loadColumnVisibility(metaColumns: string[]): Record<string, boolean> {
  const visibility: Record<string, boolean> = {};
  try {
    const raw = localStorage.getItem(VISIBILITY_KEY);
    if (!raw) return visibility;
    const stored = JSON.parse(raw) as Record<string, boolean>;
    for (const col of metaColumns) {
      const key = `meta_${col}`;
      if (key in stored) visibility[key] = stored[key];
    }
  } catch { /* corrupted — start fresh */ }
  return visibility;
}

/** Merge current visibility into global prefs and persist. */
function saveColumnVisibility(visibility: Record<string, boolean>) {
  try {
    const raw = localStorage.getItem(VISIBILITY_KEY);
    const existing = raw ? JSON.parse(raw) as Record<string, boolean> : {};
    localStorage.setItem(VISIBILITY_KEY, JSON.stringify({ ...existing, ...visibility }));
  } catch { /* silently ignore */ }
}

/**
 * Load saved column order from localStorage.
 * Returns the stored order filtered to only include keys that still exist
 * in the current column set, with any new columns appended at the end.
 */
function loadColumnOrder(currentKeys: string[]): string[] | undefined {
  try {
    const raw = localStorage.getItem(ORDER_KEY);
    if (!raw) return undefined;
    const stored = JSON.parse(raw) as string[];
    // Keep only keys that still exist, then append any new ones at the end
    const currentSet = new Set(currentKeys);
    const valid = stored.filter((k) => currentSet.has(k));
    if (valid.length === 0) return undefined;
    const seen = new Set(valid);
    const missing = currentKeys.filter((k) => !seen.has(k));
    return [...valid, ...missing];
  } catch {
    return undefined;
  }
}

/** Persist column order globally. */
function saveColumnOrder(order: string[]) {
  try {
    localStorage.setItem(ORDER_KEY, JSON.stringify(order));
  } catch { /* silently ignore */ }
}

const RISK_LEVELS: { value: RiskLevel; label: string }[] = [
  { value: 'CRITICAL', label: 'Critical' },
  { value: 'HIGH',     label: 'High' },
  { value: 'MEDIUM',   label: 'Medium' },
  { value: 'LOW',      label: 'Low' },
];

const RiskLevelHeaderFilter: React.FC<{
  selected: RiskLevel | null;
  onChange: (level: RiskLevel | null) => void;
}> = ({ selected, onChange }) => {
  const { open, pos, triggerRef, panelRef, toggle, close } = usePortalPopover({ closeOnScroll: true });

  const selectedLabel = RISK_LEVELS.find(r => r.value === selected)?.label;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
      <span style={{ fontWeight: 500, letterSpacing: '0.05em' }}>RISK LEVEL</span>
      <button
        ref={triggerRef}
        onClick={toggle}
        title={selected ? `Filter: ${selectedLabel}` : 'Filter by risk level'}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '3px',
          padding: '2px 6px',
          fontSize: '11px',
          fontWeight: 500,
          color: selected ? '#1a7090' : '#6b7280',
          background: selected ? 'linear-gradient(to bottom, #F8FEFF, #F0F9FF)' : 'transparent',
          border: selected ? '1px solid #d1e8f0' : '1px solid transparent',
          borderRadius: '4px',
          cursor: 'pointer',
        }}
      >
        {selected && <span>{selectedLabel}</span>}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            zIndex: COLUMN_HEADER_DROPDOWN_Z,
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            minWidth: '140px',
            overflow: 'hidden',
          }}
        >
          <div
            onClick={() => { onChange(null); close(); }}
            style={{
              padding: '8px 12px',
              fontSize: '13px',
              cursor: 'pointer',
              color: !selected ? '#2563eb' : '#374151',
              background: !selected ? '#eff6ff' : '#fff',
              fontWeight: !selected ? 600 : 400,
            }}
          >
            All Levels
          </div>
          {RISK_LEVELS.map(({ value, label }) => (
            <div
              key={value}
              onClick={() => { onChange(value); close(); }}
              style={{
                padding: '8px 12px',
                fontSize: '13px',
                cursor: 'pointer',
                color: selected === value ? '#2563eb' : '#374151',
                background: selected === value ? '#eff6ff' : '#fff',
                fontWeight: selected === value ? 600 : 400,
              }}
            >
              {label}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

/**
 * Truncate consent ID for display with copy functionality.
 */
const ConsentIdCell: React.FC<{ consentId: string | undefined }> = ({ consentId }) => {
  const [copied, setCopied] = React.useState(false);

  const displayId = consentId && typeof consentId === 'string' ? consentId : '';

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!displayId) return;
    try {
      await navigator.clipboard.writeText(displayId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!displayId) {
    return <span className="text-gray-400">—</span>;
  }

  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-sm truncate max-w-[180px]" title={displayId}>
        {displayId.length > 20 ? `${displayId.substring(0, 20)}...` : displayId}
      </span>
      <button
        onClick={handleCopy}
        className="p-1 hover:text-gray-600 transition-colors flex-shrink-0"
        style={{ color: '#6b7280' }}
        title="Copy to clipboard"
      >
        {copied ? '✓' : '⧉'}
      </button>
    </div>
  );
};

/**
 * Risk level chip component using Tag from equal-ds.
 */
const RiskLevelChip: React.FC<{ riskLevel: RiskLevel }> = ({ riskLevel }) => {
  const colors = getRiskLevelColor(riskLevel);

  return (
    <Tag
      size="sm"
      className={`${colors.bg} ${colors.text} ${colors.border} border`}
    >
      {riskLevel}
    </Tag>
  );
};

/**
 * Convert a normalized snake_case meta key to a display header.
 * e.g. "customer_name" → "Customer Name", "cibil_score" → "Cibil Score"
 */
const metaKeyToHeader = (key: string): string =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const EWSCustomersTable: React.FC<EWSCustomersTableProps> = ({
  customers,
  isLoading,
  pagination,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  sortBy,
  sortOrder,
  onSortChange,
  enabledMetaColumns,
  riskLevelFilter,
  onRiskLevelFilterChange,
}) => {
  // Convert parent's sortBy/sortOrder to TableSortState format
  const sortState: TableSortState | null = useMemo(() => {
    if (!sortBy) return null;
    return { column: sortBy, direction: sortOrder };
  }, [sortBy, sortOrder]);

  const handleSort = (newSortState: TableSortState | null) => {
    if (newSortState) {
      onSortChange(newSortState.column as CustomerSortColumn, newSortState.direction);
    } else {
      onSortChange(null, 'desc');
    }
  };

  const fixedColumns: TableColumn<EWSCustomer>[] = useMemo(() => [
    {
      key: 'trackingId',
      header: 'CONSENT ID',
      width: 240,
      locked: true,
      render: (_value, row) => (
        <ConsentIdCell consentId={row.trackingId || row.consentId} />
      ),
    },
    {
      key: 'currentBalance',
      header: 'CURRENT BALANCE',
      width: 160,
      sortable: true,
      accessor: (row) => formatINR(row.currentBalance),
    },
    {
      key: 'averageBalance',
      header: 'AVERAGE BALANCE',
      width: 160,
      sortable: true,
      accessor: (row) => formatINR(row.averageBalance),
    },
    {
      key: 'adjustedAverageBalance',
      header: 'ADJUSTED AVERAGE BALANCE',
      width: 200,
      sortable: true,
      accessor: (row) => formatINR(row.adjustedAverageBalance),
    },
    {
      key: 'riskLevel',
      header: 'RISK LEVEL',
      width: 160,
      renderHeader: () => (
        <RiskLevelHeaderFilter
          selected={riskLevelFilter}
          onChange={onRiskLevelFilterChange}
        />
      ),
      render: (_value, row) => <RiskLevelChip riskLevel={row.riskLevel ?? 'LOW'} />,
    },
    {
      key: 'odwMaxBalanceWindow',
      header: 'OPTIMUM WINDOW TO TRIGGER',
      width: 220,
      accessor: (row) => {
        const window = row.odwMaxBalanceWindow?.[0];
        return window ? `Day ${window.startDay}-${window.endDay}` : '-';
      },
    },
    {
      key: 'riskScore',
      header: 'RISK SCORE',
      width: 120,
      sortable: true,
      accessor: (row) => `${row.riskScore ?? 0}/100`,
    },
    {
      key: 'foir',
      header: 'FOIR',
      width: 100,
      sortable: true,
      accessor: (row) => row.foir ? row.foir.toFixed(2) : '-',
    },
    {
      key: 'activeEMIsCount',
      header: 'ACTIVE EMIs COUNT',
      width: 160,
      sortable: true,
      accessor: (row) => formatIndianNumber(row.activeEMIsCount),
    },
    {
      key: 'activeSIPs',
      header: 'ACTIVE SIPs',
      width: 120,
      sortable: true,
      accessor: (row) => formatIndianNumber(row.activeSIPs),
    },
  ], [riskLevelFilter, onRiskLevelFilterChange]);

  // Dynamic meta columns — hideable, toggled via "Manage columns"
  const metaColumnDefs: TableColumn<EWSCustomer>[] = useMemo(() => {
    if (!enabledMetaColumns || enabledMetaColumns.length === 0) return [];
    return enabledMetaColumns.map((col) => ({
      key: `meta_${col}`,
      header: metaKeyToHeader(col),
      width: 160,
      sortable: true,
      hideable: true,
      accessor: (row: EWSCustomer) => row.csvMeta?.[col] ?? '-',
    }));
  }, [enabledMetaColumns]);

  const hasMetaColumns = enabledMetaColumns && enabledMetaColumns.length > 0;

  const columns = useMemo(
    () => [...fixedColumns, ...metaColumnDefs],
    [fixedColumns, metaColumnDefs]
  );

  // --- Column visibility persistence ---
  const defaultColumnVisibility = useMemo(
    () => loadColumnVisibility(enabledMetaColumns ?? []),
    [enabledMetaColumns]
  );

  const handleColumnVisibilityChange = useCallback(
    (visibility: Record<string, boolean>) => saveColumnVisibility(visibility),
    []
  );

  // --- Column order persistence ---
  const allColumnKeys = useMemo(() => columns.map((c) => c.key), [columns]);

  const defaultColumnOrder = useMemo(
    () => loadColumnOrder(allColumnKeys),
    [allColumnKeys]
  );

  const handleColumnOrderChange = useCallback(
    (order: string[]) => saveColumnOrder(order),
    []
  );

  // Rotating loading messages
  const loadingMessages = [
    'Processing your request…',
    'Crunching the numbers…',
    'Curating results…',
    'Almost there…',
    'Delivering insights…',
  ];
  const [loadingMsgIndex, setLoadingMsgIndex] = useState(0);

  useEffect(() => {
    if (!isLoading) {
      setLoadingMsgIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setLoadingMsgIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [isLoading]);

  const handleRowClick = (row: EWSCustomer) => {
    onRowClick(row);
  };

  return (
    <div className="relative flex-1 min-h-0">
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/70 backdrop-blur-[1px] rounded-xl">
          <Loader size={48} cellSize={14} />
          <p className="mt-3 text-sm font-semibold text-gray-800">
            {loadingMessages[loadingMsgIndex]}
          </p>
        </div>
      )}
      <Table<EWSCustomer>
        className="flex-1 min-h-0 shadow-sm rounded-xl"
        data={customers}
        columns={columns}
        onRowClick={handleRowClick}
        sortState={sortState}
        onSort={handleSort}
        resizableColumns
        columnManager={hasMetaColumns ? {
          enabled: true,
          triggerLabel: 'Manage columns',
          allowHiding: true,
        } : undefined}
        defaultColumnVisibility={hasMetaColumns ? defaultColumnVisibility : undefined}
        onColumnVisibilityChange={hasMetaColumns ? handleColumnVisibilityChange : undefined}
        defaultColumnOrder={defaultColumnOrder}
        onColumnOrderChange={handleColumnOrderChange}
        pagination={{
          currentPage: pagination.currentPage,
          pageSize: pagination.pageSize,
          totalItems: pagination.totalRecords,
          onPageChange,
          onPageSizeChange,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          showPageSizeSelector: true,
          isFetching: isLoading,
        }}
      />
    </div>
  );
};

export default EWSCustomersTable;
